import { useCallback, useRef } from 'react'
import type { SyntheticEvent } from 'react'

const STORAGE_PREFIX = 'humanteq-video-progress'
const ANDROID_PREVIEW_OFFSET_SECONDS = 0.1

interface UseVideoProgressOptions {
  enabled: boolean
  userId: string | null
  src: string
  onMediaLoad?: () => void
}

function storageKey(userId: string | null, src: string): string | null {
  return userId ? `${STORAGE_PREFIX}:${userId}:${src}` : null
}

function readProgress(key: string | null): number | null {
  if (!key) return null
  try {
    const value = Number(localStorage.getItem(key))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

function writeProgress(key: string | null, value: number): boolean {
  if (!key || !Number.isFinite(value) || value <= 0) return false
  try {
    localStorage.setItem(key, String(value))
    return true
  } catch {
    return false
  }
}

function clearProgress(key: string | null): boolean {
  if (!key) return false
  try {
    localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

export function useVideoProgress({ enabled, userId, src, onMediaLoad }: UseVideoProgressOptions) {
  const key = enabled ? storageKey(userId, src) : null
  const lastSavedSecond = useRef(-1)

  const saveProgress = useCallback((video: HTMLVideoElement, force = false) => {
    const wholeSecond = Math.floor(video.currentTime)
    if (!force && wholeSecond === lastSavedSecond.current) return
    if (writeProgress(key, video.currentTime)) lastSavedSecond.current = wholeSecond
  }, [key])

  const handleLoadedMetadata = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    if (enabled) {
      // Android WebView otherwise displays the video's placeholder first frame
      // underneath its own play control, producing a double Play icon.
      event.currentTarget.currentTime = readProgress(key) ?? ANDROID_PREVIEW_OFFSET_SECONDS
    }
    onMediaLoad?.()
  }, [enabled, key, onMediaLoad])

  const handleTimeUpdate = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    saveProgress(event.currentTarget)
  }, [saveProgress])

  const handlePause = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    saveProgress(event.currentTarget, true)
  }, [saveProgress])

  const handleEnded = useCallback(() => {
    if (clearProgress(key)) lastSavedSecond.current = -1
  }, [key])

  return { handleLoadedMetadata, handleTimeUpdate, handlePause, handleEnded }
}
